import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../../utils/api';

interface Trophy {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  trophyType: string;
  isActive: boolean;
}

interface User {
  id: string;
  teamName: string;
}

interface TrophyAward {
  id: string;
  trophyId: string;
  userId: string;
  awardedAt: string;
  reason?: string;
  trophy: Trophy;
  user: User;
}

interface TrophyAwardsResponse {
  awards: TrophyAward[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

const AdminTrophyAwards: React.FC = () => {
  const { trophyId } = useParams<{ trophyId: string }>();
  const [trophy, setTrophy] = useState<Trophy | null>(null);
  const [awards, setAwards] = useState<TrophyAward[]>([]);
  const [allTrophies, setAllTrophies] = useState<Trophy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [removeAwardId, setRemoveAwardId] = useState<string | null>(null);
  
  const [selectedTrophyId, setSelectedTrophyId] = useState(trophyId || '');
  const [userSearch, setUserSearch] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [reason, setReason] = useState('');
  const [awarding, setAwarding] = useState(false);
  const [awardToAll, setAwardToAll] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchAllTrophies();
    if (trophyId) {
      fetchTrophy();
    }
  }, [trophyId]);

  useEffect(() => {
    if (selectedTrophyId) {
      fetchAwards();
    }
  }, [selectedTrophyId, currentPage]);

  useEffect(() => {
    if (userSearch.length >= 2) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [userSearch]);

  const fetchAllTrophies = async () => {
    try {
      const response = await apiClient.get('/trophies/admin/all');
      setAllTrophies(response.data);
    } catch (error) {
      console.error('Error fetching trophies:', error);
    }
  };

  const fetchTrophy = async () => {
    if (!trophyId) return;
    
    try {
      const response = await apiClient.get('/trophies/admin/all');
      const foundTrophy = response.data.find((t: Trophy) => t.id === trophyId);
      setTrophy(foundTrophy || null);
    } catch (error) {
      console.error('Error fetching trophy:', error);
    }
  };

  const fetchAwards = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20'
      });
      
      if (selectedTrophyId) {
        params.append('trophyId', selectedTrophyId);
      }

      const response = await apiClient.get(`/trophies/admin/awards?${params}`);
      const data: TrophyAwardsResponse = response.data;
      
      setAwards(data.awards);
      setTotalPages(data.pagination.pages);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to load trophy awards');
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    try {
      const response = await apiClient.get(`/users/search?q=${encodeURIComponent(userSearch)}`);
      setSearchResults(response.data.slice(0, 10)); // Limit to 10 results
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
    }
  };

  const addUser = (user: User) => {
    if (!selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers([...selectedUsers, user]);
    }
    setUserSearch('');
    setSearchResults([]);
  };

  const removeUser = (userId: string) => {
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
  };

  const handleAwardTrophy = async () => {
    if (!selectedTrophyId || (!awardToAll && selectedUsers.length === 0)) return;

    try {
      setAwarding(true);
      
      if (awardToAll) {
        // Award to all users
        await apiClient.post('/trophies/admin/award-all', {
          trophyId: selectedTrophyId,
          reason: reason.trim() || undefined
        });
      } else {
        // Award to selected users
        await apiClient.post('/trophies/admin/award', {
          trophyId: selectedTrophyId,
          userIds: selectedUsers.map(u => u.id),
          reason: reason.trim() || undefined
        });
      }

      setShowAwardModal(false);
      setSelectedUsers([]);
      setReason('');
      setUserSearch('');
      setAwardToAll(false);
      fetchAwards();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to award trophy');
    } finally {
      setAwarding(false);
    }
  };

  const handleRemoveAward = async (awardId: string) => {
    try {
      await apiClient.delete(`/trophies/admin/award/${awardId}`);
      setRemoveAwardId(null);
      fetchAwards();
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to remove trophy award');
    }
  };

  if (loading && awards.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highlight"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center space-x-4">
              <Link 
                to="/admin/trophies"
                className="text-highlight hover:underline"
              >
                ← Back to Trophies
              </Link>
            </div>
            <h1 className="text-3xl font-bold mt-2" style={{ color: 'var(--text-primary)' }}>
              Trophy Awards
            </h1>
            {trophy && (
              <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
                Managing awards for: {trophy.name}
              </p>
            )}
          </div>
          <button
            onClick={() => setShowAwardModal(true)}
            className="btn-primary px-4 py-2"
          >
            Award Trophy
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4 mb-6">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Trophy Filter */}
      {!trophyId && (
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            Filter by Trophy
          </label>
          <select
            value={selectedTrophyId}
            onChange={(e) => {
              setSelectedTrophyId(e.target.value);
              setCurrentPage(1);
            }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
            style={{ color: 'var(--text-primary)' }}
          >
            <option value="">All Trophies</option>
            {allTrophies.map(trophy => (
              <option key={trophy.id} value={trophy.id}>
                {trophy.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Awards Table */}
      <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Trophy
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Reason
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Awarded
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {awards.map((award) => (
              <tr key={award.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {award.trophy.imageUrl && (
                      <img
                        src={award.trophy.imageUrl}
                        alt={award.trophy.name}
                        className="h-8 w-8 rounded-full mr-3 object-cover"
                      />
                    )}
                    <div>
                      <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {award.trophy.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link
                    to={`/users/${award.user.id}`}
                    className="text-sm font-medium hover:underline"
                    style={{ color: 'var(--highlight)' }}
                  >
                    {award.user.teamName}
                  </Link>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {award.reason || '-'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {new Date(award.awardedAt).toLocaleDateString()} {new Date(award.awardedAt).toLocaleTimeString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => setRemoveAwardId(award.id)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {awards.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              No trophy awards
            </h3>
            <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
              No trophies have been awarded yet.
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-6 space-x-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="btn-secondary px-3 py-2 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="flex items-center px-3 py-2" style={{ color: 'var(--text-primary)' }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="btn-secondary px-3 py-2 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Award Trophy Modal */}
      {showAwardModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Award Trophy
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Trophy
                </label>
                <select
                  value={selectedTrophyId}
                  onChange={(e) => setSelectedTrophyId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                  style={{ color: 'var(--text-primary)' }}
                  required
                >
                  <option value="">Select a trophy</option>
                  {allTrophies.filter(t => t.isActive).map(trophy => (
                    <option key={trophy.id} value={trophy.id}>
                      {trophy.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Award to All Users Option */}
              <div className="flex items-center mb-4">
                <input
                  type="checkbox"
                  id="awardToAll"
                  checked={awardToAll}
                  onChange={(e) => {
                    setAwardToAll(e.target.checked);
                    if (e.target.checked) {
                      setSelectedUsers([]);
                      setUserSearch('');
                    }
                  }}
                  className="mr-2"
                />
                <label htmlFor="awardToAll" className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                  Award to ALL users (entire user base)
                </label>
              </div>

              {!awardToAll && (
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Search Users
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      placeholder="Type team name to search..."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                      style={{ color: 'var(--text-primary)' }}
                    />
                    {searchResults.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-40 overflow-y-auto">
                        {searchResults.map(user => (
                          <button
                            key={user.id}
                            onClick={() => addUser(user)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600"
                            style={{ color: 'var(--text-primary)' }}
                          >
                            {user.teamName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!awardToAll && selectedUsers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                    Selected Users
                  </label>
                  <div className="space-y-1">
                    {selectedUsers.map(user => (
                      <div key={user.id} className="flex items-center justify-between bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded">
                        <span style={{ color: 'var(--text-primary)' }}>{user.teamName}</span>
                        <button
                          onClick={() => removeUser(user.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {awardToAll && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-yellow-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      This will award the trophy to ALL users in the system
                    </span>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>
                  Reason (Optional)
                </label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Winner of January 2024"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                  style={{ color: 'var(--text-primary)' }}
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <button
                  onClick={() => {
                    setShowAwardModal(false);
                    setSelectedUsers([]);
                    setReason('');
                    setUserSearch('');
                    setAwardToAll(false);
                  }}
                  className="btn-secondary px-4 py-2"
                  disabled={awarding}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAwardTrophy}
                  className="btn-primary px-4 py-2"
                  disabled={awarding || !selectedTrophyId || (!awardToAll && selectedUsers.length === 0)}
                >
                  {awarding 
                    ? 'Awarding...' 
                    : awardToAll 
                      ? 'Award to ALL Users' 
                      : `Award to ${selectedUsers.length} User(s)`
                  }
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Award Modal */}
      {removeAwardId && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              Remove Trophy Award
            </h3>
            <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
              Are you sure you want to remove this trophy award?
            </p>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setRemoveAwardId(null)}
                className="btn-secondary px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRemoveAward(removeAwardId)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTrophyAwards;